import { FormEvent, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Box, Button, TextField, Alert, Link } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { api, getErrorMessage } from "../api/client";
import AuthCard from "../components/AuthCard";
import { useAuth } from "../contexts/AuthContext";

export default function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  const accept = useMutation({
    mutationFn: async () => {
      await api.acceptInvitation(token!, { display_name: displayName, password });
      await refreshUser();
    },
    meta: { suppressErrorToast: true },
    onSuccess: () => navigate("/", { replace: true }),
    onError: (err) => setError(getErrorMessage(err)),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    accept.mutate();
  }

  return (
    <AuthCard title="Create your account" subtitle="You've been invited to Vayken">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          {error.includes("already been used") && (
            <>
              {" "}
              <Link component="button" type="button" onClick={() => navigate("/login")}>
                Go to login
              </Link>
            </>
          )}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          label="Display name"
          fullWidth
          required
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Password"
          type="password"
          fullWidth
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Confirm password"
          type="password"
          fullWidth
          required
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Button type="submit" variant="contained" fullWidth loading={accept.isPending} sx={{ mt: 1 }}>
          Create account
        </Button>
      </Box>
    </AuthCard>
  );
}
